// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useCallback } from "react";
import { X, Upload, FileText } from "lucide-react";
import { productService } from "../../../services/productService";

const ALLOWED_EXTENSIONS = [".csv", ".xlsx", ".json"];

function inferFileType(filename) {
  if (!filename) return null;
  const lower = filename.toLowerCase();
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".xlsx")) return "xlsx";
  if (lower.endsWith(".json")) return "json";
  return null;
}

const ImportProductsModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState("");
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [validateOnly, setValidateOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const reset = useCallback(() => {
    setFile(null);
    setFileType("");
    setOverwriteExisting(false);
    setValidateOnly(false);
    setError(null);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleFileSelect = useCallback((selectedFile) => {
    setError(null);
    setResult(null);
    const inferred = inferFileType(selectedFile?.name);
    if (!inferred) {
      setError("Ungültiges Format. Erlaubt: CSV, XLSX oder JSON.");
      setFile(null);
      setFileType("");
      return;
    }
    setFile(selectedFile);
    setFileType(inferred);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) handleFileSelect(f);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      const f = e.target?.files?.[0];
      if (f) handleFileSelect(f);
      e.target.value = "";
    },
    [handleFileSelect],
  );

  const handleSubmit = useCallback(async () => {
    if (!file || !fileType) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await productService.importProducts(file, fileType, {
        overwriteExisting,
        validateOnly,
      });
      setResult(res);
      if (!validateOnly && res?.success && onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const data = err.response?.data ?? {};
      const detail = data.detail;
      const message =
        data.message ||
        (detail && typeof detail === "object" && detail.message
          ? detail.message
          : typeof detail === "string"
            ? detail
            : undefined) ||
        (Array.isArray(data.errors)
          ? data.errors.map((e) => e.msg ?? e.message).join(", ")
          : null) ||
        err.message ||
        "Import fehlgeschlagen.";
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [file, fileType, overwriteExisting, validateOnly, onSuccess]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="relative mx-4 w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Katalog importieren
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-140px)] overflow-y-auto p-4">
          <p className="mb-4 text-sm text-gray-600">
            Unterstützte Formate: CSV, XLSX, JSON. Erforderliche Spalten:{" "}
            <code className="rounded bg-gray-100 px-1">vhp_name</code>,{" "}
            <code className="rounded bg-gray-100 px-1">vhp_product_type</code>,{" "}
            <code className="rounded bg-gray-100 px-1">type</code>.
          </p>

          {/* Drag-and-drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`mb-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
              isDragging
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-gray-50 hover:border-gray-400"
            }`}
          >
            <Upload className="mb-2 h-10 w-10 text-gray-400" />
            <p className="mb-2 text-sm text-gray-600">
              Datei hierher ziehen oder{" "}
              <label className="cursor-pointer font-medium text-blue-600 hover:text-blue-800">
                Datei auswählen
                <input
                  type="file"
                  accept={ALLOWED_EXTENSIONS.join(",")}
                  onChange={handleInputChange}
                  className="sr-only"
                />
              </label>
            </p>
            {file && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <FileText className="h-4 w-4" />
                {file.name} ({fileType.toUpperCase()})
              </p>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3 border-t border-gray-200 pt-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={validateOnly}
                onChange={(e) => setValidateOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Nur validieren (Vorschau) – keine Daten werden geschrieben
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Vorhandene Einträge überschreiben (inkrementelle Updates)
              </span>
            </label>
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Import report */}
          {result && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                Import-Bericht
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                <div className="rounded bg-white p-2 shadow-sm">
                  <span className="text-gray-500">Zeilen gesamt</span>
                  <p className="font-medium">{result.total_rows ?? 0}</p>
                </div>
                <div className="rounded bg-white p-2 shadow-sm">
                  <span className="text-gray-500">Importiert</span>
                  <p className="font-medium text-green-600">
                    {result.imported_count ?? 0}
                  </p>
                </div>
                <div className="rounded bg-white p-2 shadow-sm">
                  <span className="text-gray-500">Aktualisiert</span>
                  <p className="font-medium text-blue-600">
                    {result.updated_count ?? 0}
                  </p>
                </div>
                <div className="rounded bg-white p-2 shadow-sm">
                  <span className="text-gray-500">Übersprungen</span>
                  <p className="font-medium text-gray-600">
                    {result.skipped_count ?? 0}
                  </p>
                </div>
                <div className="rounded bg-white p-2 shadow-sm">
                  <span className="text-gray-500">Fehler</span>
                  <p className="font-medium text-red-600">
                    {result.error_count ?? 0}
                  </p>
                </div>
              </div>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-600">
                    Fehlerdetails (Zeile, Meldung)
                  </p>
                  <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-gray-200 bg-white p-2 text-xs">
                    {result.errors.slice(0, 50).map((err, i) => (
                      <li
                        key={i}
                        className="border-b border-gray-100 py-1 last:border-0"
                      >
                        Zeile {err.row}: {err.error}
                      </li>
                    ))}
                    {result.errors.length > 50 && (
                      <li className="py-1 text-gray-500">
                        … und {result.errors.length - 50} weitere
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {Array.isArray(result.warnings) && result.warnings.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-600">Warnungen</p>
                  <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-amber-200 bg-amber-50 p-2 text-xs">
                    {result.warnings.slice(0, 20).map((w, i) => (
                      <li
                        key={i}
                        className="border-b border-amber-100 py-1 last:border-0"
                      >
                        Zeile {w.row}: {w.message}
                      </li>
                    ))}
                    {result.warnings.length > 20 && (
                      <li className="py-1 text-gray-500">
                        … und {result.warnings.length - 20} weitere
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
          >
            Schließen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!file || !fileType || loading}
            className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {validateOnly ? "Validiere…" : "Importiere…"}
              </>
            ) : validateOnly ? (
              "Nur validieren"
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Import starten
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportProductsModal;
